import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import RedeemIcon from "@mui/icons-material/Redeem";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import StarIcon from "@mui/icons-material/Star";
import ScienceIcon from "@mui/icons-material/Science";
import MenuIpCopySnackbar from "./MenuIpCopySnackbar";
import UserMenu from "./UserMenu";
import ForumNotificationsBell from "./ForumNotificationsBell";
import { Link as RouterLink } from "react-router-dom";
import { mainNavPages as pages } from "../config/mainNav";

const siteIconSx = {
  mr: 1,
  width: 32,
  height: 32,
  flexShrink: 0,
};

function ResponsiveAppBar() {
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  // Generic anchor map for desktop dropdowns: { [pageName]: DOMElement | null }
  const [anchorEls, setAnchorEls] = React.useState({});
  const [desktopSubmenuAnchor, setDesktopSubmenuAnchor] = React.useState(null);
  const [desktopSubmenuItem, setDesktopSubmenuItem] = React.useState(null);
  const [mobileSubmenuAnchor, setMobileSubmenuAnchor] = React.useState(null);
  const [mobileSubmenuPage, setMobileSubmenuPage] = React.useState(null);
  const [mobileNestedSubmenuAnchor, setMobileNestedSubmenuAnchor] = React.useState(null);
  const [mobileNestedSubmenuItem, setMobileNestedSubmenuItem] = React.useState(null);
  const desktopSubmenuOpenTimerRef = React.useRef(null);
  const desktopSubmenuCloseTimerRef = React.useRef(null);
  /** Keeps latest `anchorEls` for toggle / pointer handlers. */
  const anchorElsRef = React.useRef(anchorEls);
  /** Nested Game Servers flyout paper — used to detect pointer moving from parent menu into the flyout. */
  const desktopNestedMenuPaperRef = React.useRef(null);
  const desktopSubmenuAnchorRef = React.useRef(null);
  const desktopSubmenuItemRef = React.useRef(null);
  /** Each desktop dropdown Menu paper, keyed by `page.name` (avoids querySelector grabbing the wrong menu). */
  const desktopDropdownPaperRefs = React.useRef({});

  React.useLayoutEffect(() => {
    anchorElsRef.current = anchorEls;
  }, [anchorEls]);

  React.useLayoutEffect(() => {
    desktopSubmenuAnchorRef.current = desktopSubmenuAnchor;
  }, [desktopSubmenuAnchor]);

  React.useLayoutEffect(() => {
    desktopSubmenuItemRef.current = desktopSubmenuItem;
  }, [desktopSubmenuItem]);

  const clearTimer = (timerRef) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const clearDesktopSubmenuOpenTimer = () => clearTimer(desktopSubmenuOpenTimerRef);
  const clearDesktopSubmenuCloseTimer = () => clearTimer(desktopSubmenuCloseTimerRef);

  const closeAllDesktopDropdowns = () => {
    clearDesktopSubmenuOpenTimer();
    clearDesktopSubmenuCloseTimer();
    desktopSubmenuAnchorRef.current = null;
    desktopSubmenuItemRef.current = null;
    setDesktopSubmenuAnchor(null);
    setDesktopSubmenuItem(null);
    setAnchorEls({});
  };

  React.useEffect(() => {
    return () => {
      clearTimeout(desktopSubmenuOpenTimerRef.current);
      clearTimeout(desktopSubmenuCloseTimerRef.current);
    };
  }, []);

  /** Click-target is still inside this menu's trigger, panel, or nested flyout (for click-outside). */
  const isPointerInsideDesktopMenuHitZone = (name, node) => {
    if (!(node instanceof Node)) return false;
    const anchor = anchorElsRef.current[name];
    if (anchor && typeof anchor.contains === "function" && anchor.contains(node)) {
      return true;
    }
    const paper = desktopDropdownPaperRefs.current[name];
    if (paper && typeof paper.contains === "function" && paper.contains(node)) {
      return true;
    }
    if (
      desktopNestedMenuPaperRef.current &&
      typeof desktopNestedMenuPaperRef.current.contains === "function" &&
      desktopNestedMenuPaperRef.current.contains(node)
    ) {
      return true;
    }
    return false;
  };

  /** Click outside desktop dropdowns / nested flyout closes everything (backdrop is non-interactive). */
  React.useEffect(() => {
    const hasDropdown = Object.values(anchorEls).some(Boolean);
    const nestedOpen = Boolean(desktopSubmenuAnchor);
    if (!hasDropdown && !nestedOpen) {
      return undefined;
    }

    const onPointerDownCapture = (event) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      const openDesktopName = Object.keys(anchorElsRef.current).find((k) => anchorElsRef.current[k]);
      if (openDesktopName && isPointerInsideDesktopMenuHitZone(openDesktopName, target)) {
        return;
      }

      closeAllDesktopDropdowns();
    };

    document.addEventListener("pointerdown", onPointerDownCapture, true);
    return () => document.removeEventListener("pointerdown", onPointerDownCapture, true);
  }, [anchorEls, desktopSubmenuAnchor]);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseMenu = (name) => () => {
    clearDesktopSubmenuOpenTimer();
    clearDesktopSubmenuCloseTimer();
    setAnchorEls((prev) => ({ ...prev, [name]: null }));
    desktopSubmenuAnchorRef.current = null;
    desktopSubmenuItemRef.current = null;
    setDesktopSubmenuAnchor(null);
    setDesktopSubmenuItem(null);
  };

  const openDesktopMenu = (name, anchorEl) => {
    clearDesktopSubmenuOpenTimer();
    clearDesktopSubmenuCloseTimer();
    desktopSubmenuAnchorRef.current = null;
    desktopSubmenuItemRef.current = null;
    setDesktopSubmenuAnchor(null);
    setDesktopSubmenuItem(null);
    setAnchorEls({ [name]: anchorEl });
  };

  const handleToggleDesktopMenu = (name) => (event) => {
    if (anchorElsRef.current[name]) {
      handleCloseMenu(name)();
      return;
    }
    openDesktopMenu(name, event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
    setMobileSubmenuAnchor(null);
    setMobileSubmenuPage(null);
    setMobileNestedSubmenuAnchor(null);
    setMobileNestedSubmenuItem(null);
  };

  const handleOpenMobileSubmenu = (page) => (event) => {
    setMobileNestedSubmenuAnchor(null);
    setMobileNestedSubmenuItem(null);
    setMobileSubmenuAnchor(event.currentTarget);
    setMobileSubmenuPage(page);
  };

  const handleCloseMobileSubmenu = () => {
    setMobileSubmenuAnchor(null);
    setMobileSubmenuPage(null);
    setMobileNestedSubmenuAnchor(null);
    setMobileNestedSubmenuItem(null);
  };

  const handleOpenDesktopSubmenu = (item) => (event) => {
    clearDesktopSubmenuOpenTimer();
    clearDesktopSubmenuCloseTimer();
    const el = event.currentTarget;
    desktopSubmenuAnchorRef.current = el;
    desktopSubmenuItemRef.current = item;
    setDesktopSubmenuAnchor(el);
    setDesktopSubmenuItem(item);
  };

  const handleScheduleCloseDesktopSubmenu = () => {
    clearDesktopSubmenuOpenTimer();
    clearDesktopSubmenuCloseTimer();
    desktopSubmenuCloseTimerRef.current = setTimeout(() => {
      desktopSubmenuAnchorRef.current = null;
      desktopSubmenuItemRef.current = null;
      setDesktopSubmenuAnchor(null);
      setDesktopSubmenuItem(null);
      desktopSubmenuCloseTimerRef.current = null;
    }, 250);
  };

  const handleCancelScheduledDesktopSubmenuClose = () => {
    clearDesktopSubmenuCloseTimer();
  };

  const handleCloseDesktopSubmenu = () => {
    clearDesktopSubmenuOpenTimer();
    clearDesktopSubmenuCloseTimer();
    desktopSubmenuAnchorRef.current = null;
    desktopSubmenuItemRef.current = null;
    setDesktopSubmenuAnchor(null);
    setDesktopSubmenuItem(null);
  };

  const handleOpenMobileNestedSubmenu = (item) => (event) => {
    setMobileNestedSubmenuAnchor(event.currentTarget);
    setMobileNestedSubmenuItem(item);
  };

  const handleCloseMobileNestedSubmenu = () => {
    setMobileNestedSubmenuAnchor(null);
    setMobileNestedSubmenuItem(null);
  };

  const getItemIcon = (icon) => {
    switch (icon) {
      case "vote":
        return <HowToVoteIcon fontSize="small" />;
      case "store":
        return <RedeemIcon fontSize="small" />;
      case "star":
        return <StarIcon fontSize="small" />;
      case "science":
        return <ScienceIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const itemHasSubmenu = (item) =>
    Array.isArray(item.items) && item.items.length > 0
      ? true
      : Array.isArray(item.submenuColumns) && item.submenuColumns.length > 0;

  const getItemTextSx = (item) => {
    if (item.isSection) {
      return {
        fontSize: item.preserveCase ? "0.9rem" : "0.72rem",
        fontWeight: 700,
        textTransform: item.preserveCase ? "none" : "uppercase",
        letterSpacing: item.preserveCase ? "0.01em" : "0.08em",
      };
    }

    if (item.preserveCase) {
      return {
        fontWeight: 600,
      };
    }

    return undefined;
  };

  const renderItemContent = (item) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {getItemIcon(item.icon)}
      <Typography component="span" sx={getItemTextSx(item)}>
        {item.name}
      </Typography>
    </Box>
  );

  // Render section header items — optionally clickable
  const renderSectionHeader = (item, closeHandler, key, extraSx = {}) => {
    const base = {
      py: item.preserveCase ? 1 : 0.75,
      minHeight: "unset",
      ...extraSx,
    };

    if (item.url) {
      return (
        <MenuItem
          key={key}
          component={RouterLink}
          to={item.url}
          onClick={closeHandler}
          sx={{
            ...base,
            color: item.preserveCase ? "text.primary" : "primary.main",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          {renderItemContent(item)}
        </MenuItem>
      );
    }

    return (
      <MenuItem
        key={key}
        disabled
        sx={{ ...base, color: "text.secondary", opacity: "1 !important" }}
      >
        {renderItemContent(item)}
      </MenuItem>
    );
  };

  const renderStandardItem = (item, closeHandler, key, extraSx = {}) => {
    const sx = { py: item.preserveCase ? 1.25 : 1.5, px: 2.5, ...extraSx };

    if (item.external) {
      return (
        <MenuItem
          key={key}
          component="a"
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeHandler}
          sx={sx}
        >
          {renderItemContent(item)}
        </MenuItem>
      );
    }

    return (
      <MenuItem
        key={key}
        component={RouterLink}
        to={item.url}
        onClick={closeHandler}
        sx={sx}
      >
        {renderItemContent(item)}
      </MenuItem>
    );
  };

  const renderSubmenuTriggerItem = (
    item,
    openHandler,
    key,
    extraSx = {},
    hoverOpenHandler = null,
    hoverCloseHandler = null
  ) => (
    <MenuItem
      key={key}
      onClick={openHandler}
      onMouseEnter={hoverOpenHandler}
      onMouseLeave={hoverCloseHandler ? hoverCloseHandler(item) : null}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        py: item.preserveCase ? 1.25 : 1.5,
        px: 2.5,
        gap: 1.5,
        ...extraSx,
      }}
    >
      {renderItemContent(item)}
      <ChevronRightIcon fontSize="small" sx={{ color: "text.secondary" }} />
    </MenuItem>
  );

  const renderSpacerItem = (item, key, spacerSx = {}) => {
    if (item.spacerSize === "divider") {
      return <Divider key={key} sx={{ visibility: "hidden" }} aria-hidden />;
    }

    if (item.spacerSize === "section") {
      return (
        <MenuItem
          key={key}
          disabled
          aria-hidden
          sx={{
            py: 0.75,
            minHeight: "unset",
            visibility: "hidden",
            pointerEvents: "none",
            ...spacerSx,
          }}
        >
          <Typography component="span" sx={{ fontSize: "0.72rem" }}>
            &nbsp;
          </Typography>
        </MenuItem>
      );
    }

    return (
      <MenuItem
        key={key}
        disabled
        aria-hidden
        sx={{
          py: 1.5,
          px: 2.5,
          visibility: "hidden",
          pointerEvents: "none",
          ...spacerSx,
        }}
      >
        &nbsp;
      </MenuItem>
    );
  };

  const renderMenuItems = (
    items,
    closeHandler,
    keyPrefix,
    extraSx = {},
    submenuOpenHandler = null,
    submenuHoverOpenHandler = null,
    submenuHoverCloseHandler = null,
    { hideSpacers = false } = {}
  ) =>
    items.flatMap((item, index) => {
      if (item.isSpacer) {
        if (hideSpacers) {
          return [];
        }

        return [renderSpacerItem(item, `${keyPrefix}-spacer-${index}`, extraSx)];
      }

      if (item.isSection) {
        const nodes = [];
        if (index > 0) {
          nodes.push(<Divider key={`${keyPrefix}-${item.name}-divider`} />);
        }
        nodes.push(
          renderSectionHeader(item, closeHandler, `${keyPrefix}-${item.name}-section`, extraSx)
        );
        return nodes;
      }

      if (itemHasSubmenu(item) && submenuOpenHandler) {
        return [
          renderSubmenuTriggerItem(
            item,
            submenuOpenHandler(item),
            `${keyPrefix}-${item.name}-submenu`,
            extraSx,
            submenuHoverOpenHandler ? submenuHoverOpenHandler(item) : null,
            submenuHoverCloseHandler
          ),
        ];
      }

      return [renderStandardItem(item, closeHandler, `${keyPrefix}-${item.name}-item`, extraSx)];
    });

  const renderColumnMenuItems = (
    columns,
    closeHandler,
    keyPrefix,
    extraSx = {},
    { singleColumn = false } = {}
  ) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: singleColumn ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
        alignItems: "start",
      }}
    >
      {columns.map((column, index) => (
        <Box
          key={`${keyPrefix}-column-${index}`}
          sx={{
            py: 1,
            minWidth: singleColumn ? 0 : 220,
            borderLeft: !singleColumn && index > 0 ? 1 : 0,
            borderTop: singleColumn && index > 0 ? 1 : 0,
            borderColor: "divider",
          }}
        >
          {renderMenuItems(
            column,
            closeHandler,
            `${keyPrefix}-column-${index}`,
            extraSx,
            null,
            null,
            null,
            { hideSpacers: singleColumn }
          )}
        </Box>
      ))}
    </Box>
  );

  const renderNestedMenuContent = (
    item,
    closeHandler,
    keyPrefix,
    extraSx = {},
    layout = {}
  ) => {
    if (item?.submenuColumns) {
      return renderColumnMenuItems(item.submenuColumns, closeHandler, keyPrefix, extraSx, layout);
    }

    if (item?.items) {
      return renderMenuItems(item.items, closeHandler, keyPrefix, extraSx);
    }

    return null;
  };

  const getNestedMenuPaperSx = (item, { singleColumn = false } = {}) => ({
    minWidth: item?.submenuColumns ? (singleColumn ? 220 : 460) : 220,
    maxWidth: "calc(100vw - 24px)",
  });

  return (
    <AppBar position="static" color="header">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <noscript>
            <Box sx={{ mr: 2, flexShrink: 0 }}>
              <Typography
                component="a"
                href="/site-menu"
                sx={{ color: "#fff", fontWeight: 700, textDecoration: "underline" }}
              >
                Site menu (no JavaScript)
              </Typography>
            </Box>
          </noscript>
          {/* Desktop logo */}
          <Box
            component={RouterLink}
            to="/"
            sx={{
              mr: 2,
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
              textDecoration: "none",
              color: "#fff",
            }}
          >
            <Box
              component="img"
              src="/server-icon.png"
              alt=""
              aria-hidden
              sx={{ ...siteIconSx, mr: 1 }}
            />
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontFamily: "monospace",
                fontWeight: 700,
                letterSpacing: ".1rem",
                color: "inherit",
              }}
            >
              MCVerse
            </Typography>
          </Box>

          {/* Mobile hamburger */}
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: "flex", lg: "none" },
              color: "#ffffff",
            }}
          >
            <IconButton
              size="large"
              aria-label="navigation menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>

            {/* Primary mobile menu */}
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              keepMounted
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{ display: { xs: "block", lg: "none" } }}
            >
              {pages.map((page) => {
                if (page.items) {
                  return (
                    <MenuItem
                      key={page.name}
                      onClick={handleOpenMobileSubmenu(page)}
                      sx={{ display: "flex", justifyContent: "space-between", fontWeight: "bold" }}
                    >
                      <Typography textAlign="left">{page.name}</Typography>
                      <ChevronRightIcon fontSize="small" sx={{ ml: 1 }} />
                    </MenuItem>
                  );
                }

                return renderStandardItem(page, handleCloseNavMenu, `mobile-${page.name}`, {
                  px: 2.5,
                });
              })}
            </Menu>

            <Menu
              anchorEl={mobileSubmenuAnchor}
              open={Boolean(mobileSubmenuAnchor)}
              onClose={handleCloseMobileSubmenu}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              PaperProps={{ sx: { minWidth: 220 } }}
              sx={{ display: { xs: "block", lg: "none" } }}
            >
              {mobileSubmenuPage
                ? renderMenuItems(
                    mobileSubmenuPage.items,
                    handleCloseNavMenu,
                    `mobile-${mobileSubmenuPage.name}`,
                    {
                      px: 2.25,
                    },
                    handleOpenMobileNestedSubmenu
                  )
                : null}
            </Menu>

            <Menu
              anchorEl={mobileNestedSubmenuAnchor}
              open={Boolean(mobileNestedSubmenuAnchor)}
              onClose={handleCloseMobileNestedSubmenu}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              PaperProps={{
                sx: getNestedMenuPaperSx(mobileNestedSubmenuItem, { singleColumn: true }),
              }}
              sx={{ display: { xs: "block", lg: "none" } }}
            >
              {mobileNestedSubmenuItem
                ? renderNestedMenuContent(
                    mobileNestedSubmenuItem,
                    handleCloseNavMenu,
                    `mobile-nested-${mobileNestedSubmenuItem.name}`,
                    {
                      px: 2.25,
                    },
                    { singleColumn: true }
                  )
                : null}
            </Menu>
          </Box>

          {/* Mobile logo */}
          <Box
            component={RouterLink}
            to="/"
            sx={{
              mr: 2,
              display: { xs: "flex", lg: "none" },
              flexGrow: 1,
              alignItems: "center",
              textDecoration: "none",
              color: "#ffffff",
            }}
          >
            <Box
              component="img"
              src="/server-icon.png"
              alt=""
              aria-hidden
              sx={{ ...siteIconSx, mr: 1 }}
            />
            <Typography
              variant="h5"
              noWrap
              sx={{
                fontFamily: "monospace",
                fontWeight: 400,
                letterSpacing: ".1rem",
                color: "inherit",
              }}
            >
              MCVerse
            </Typography>
          </Box>

          {/* Desktop nav */}
          <Box sx={{ flexGrow: 1, display: { xs: "none", lg: "flex" } }}>
            {pages.flatMap((page) => {
              if (page.items) {
                const isOpen = Boolean(anchorEls[page.name]);
                return [
                  <Button
                    key={`${page.name}-button`}
                    type="button"
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    onClick={handleToggleDesktopMenu(page.name)}
                    endIcon={
                      isOpen ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )
                    }
                    sx={{ my: 2, color: "white", display: "flex", px: 1 }}
                  >
                    {page.name}
                  </Button>,
                  <Menu
                    key={`${page.name}-menu`}
                    anchorEl={anchorEls[page.name]}
                    open={isOpen}
                    onClose={handleCloseMenu(page.name)}
                    disableScrollLock
                    disableAutoFocus
                    disableEnforceFocus
                    disableRestoreFocus
                    BackdropProps={{
                      invisible: true,
                      sx: { pointerEvents: "none" },
                    }}
                    PaperProps={{
                      ref: (el) => {
                        if (el) {
                          desktopDropdownPaperRefs.current[page.name] = el;
                        } else {
                          delete desktopDropdownPaperRefs.current[page.name];
                        }
                      },
                      sx: { minWidth: 200 },
                    }}
                  >
                    {renderMenuItems(
                      page.items,
                      handleCloseMenu(page.name),
                      `desktop-${page.name}`,
                      {},
                      handleOpenDesktopSubmenu,
                      null,
                      null
                    )}
                  </Menu>,
                ];
              }

              if (page.external) {
                return (
                  <Button
                    key={page.name}
                    href={page.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={getItemIcon(page.icon) || undefined}
                    sx={{ my: 2, px: 2, color: "white", display: "flex" }}
                  >
                    {page.name}
                  </Button>
                );
              }

              return (
                <Button
                  key={page.name}
                  component={RouterLink}
                  to={page.url}
                  startIcon={getItemIcon(page.icon) || undefined}
                  sx={{ my: 2, px: 2, color: "white", display: "flex" }}
                >
                  {page.name}
                </Button>
              );
            })}
          </Box>

          {/* Second-level server links: Popper (not Menu/Modal) so the flyout does not stack a second modal. */}
          <Popper
            open={Boolean(desktopSubmenuAnchor)}
            anchorEl={desktopSubmenuAnchor}
            placement="right-start"
            strategy="fixed"
            sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
            modifiers={[
              {
                name: "offset",
                options: {
                  offset: [0, 0],
                },
              },
            ]}
          >
            <Paper
              ref={desktopNestedMenuPaperRef}
              variant="outlined"
              elevation={8}
              onMouseEnter={handleCancelScheduledDesktopSubmenuClose}
              onMouseLeave={handleScheduleCloseDesktopSubmenu}
              sx={{
                ...getNestedMenuPaperSx(desktopSubmenuItem),
                maxHeight: "min(70vh, 480px)",
                overflow: "auto",
              }}
            >
              {desktopSubmenuItem
                ? renderNestedMenuContent(
                    desktopSubmenuItem,
                    handleCloseMenu("Game Servers"),
                    `desktop-nested-${desktopSubmenuItem.name}`
                  )
                : null}
            </Paper>
          </Popper>

          <Box sx={{ flexGrow: 0, display: "flex", alignItems: "center", gap: 0.25 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <MenuIpCopySnackbar />
            </Box>
            <UserMenu />
            <ForumNotificationsBell />
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default ResponsiveAppBar;
